import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ElementRef, HostListener } from '@angular/core';
import * as _ from 'lodash';
import { EventWebhookService, EventDefinition } from '../../core/services/core.event-webhook.service';

const CATEGORY_LABELS: { [k: string]: string } = {
    account: 'Account',
    build:   'Build',
    docman:  'Documents',
    forum:   'Forum',
    frs:     'File Releases',
    scm:     'Source Code',
    tracker: 'Issue Tracker',
};

const ENTITY_LABELS: { [k: string]: string } = {
    document:    'Document',
    message:     'Message',
    package:     'Package',
    release:     'Release',
    branch:      'Branch',
    merge:       'Merge',
    push:        'Push / Commit',
    result:      'Build Result',
    tag:         'Tag',
    trackeritem: 'Tracker Item',
    user:        'User',
};

interface EntityGroup {
    entity: string;
    entityLabel: string;
    wildcardType: string;
    events: EventDefinition[];
}

interface CategoryGroup {
    category: string;
    categoryLabel: string;
    wildcardType: string;
    entityGroups: EntityGroup[];
}

@Component({
    selector: 'event-subscription-edit',
    template: require('./event-subscription-edit.component.html'),
})
export class EventSubscriptionEditComponent implements OnInit, OnChanges {
    @Input() subscriptionId: number = 0;
    @Input() projectId: number | null = null;
    @Output() saved = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    loading = false;
    saving = false;
    error: string | null = null;
    savedSecret: string | null = null;
    secretCopied = false;
    showDetails = false;

    pickerOpen = false;
    filterText = '';

    catalog: EventDefinition[] = [];
    catalogGroups: CategoryGroup[] = [];
    selectedEvent: EventDefinition | null = null;

    get filteredGroups(): CategoryGroup[] {
        const q = this.filterText.trim().toLowerCase();
        if (!q) { return this.catalogGroups; }
        return this.catalogGroups.reduce((result, group) => {
            const entityGroups = group.entityGroups.reduce((egResult, eg) => {
                const events = eg.events.filter(ev =>
                    ev.type.includes(q) || ev.description.toLowerCase().includes(q)
                );
                const showWild = eg.wildcardType.includes(q) || eg.entityLabel.toLowerCase().includes(q);
                if (showWild || events.length > 0) { egResult.push({ ...eg, events }); }
                return egResult;
            }, [] as EntityGroup[]);
            const showCatWild = group.wildcardType.includes(q) || group.categoryLabel.toLowerCase().includes(q);
            if (showCatWild || entityGroups.length > 0) { result.push({ ...group, entityGroups }); }
            return result;
        }, [] as CategoryGroup[]);
    }

    form = {
        name: '',
        enabled: true,
        event_pattern: '',
        actionType: 'webhook' as 'webhook' | 'email' | 'llm',
        // webhook fields
        url: '',
        method: 'POST',
        // email fields
        recipients: '',
        subjectTemplate: '',
        bodyTemplate: '',
        // llm fields
        promptTemplate: '',
        // shared
        retention_days: 30,
        overrideRetry: false,
        max_attempts: 3,
        backoff_seconds: [60, 300] as number[],
    };

    httpMethods = ['POST', 'GET', 'PUT'];

    constructor(private service: EventWebhookService, private el: ElementRef) {}

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.el.nativeElement.contains(event.target as Node)) {
            this.pickerOpen = false;
            this.filterText = '';
        }
    }

    togglePicker() {
        this.pickerOpen = !this.pickerOpen;
        if (!this.pickerOpen) { this.filterText = ''; }
    }

    selectPattern(type: string) {
        this.form.event_pattern = type;
        this.onPatternChange(type);
        this.pickerOpen = false;
        this.filterText = '';
    }

    ngOnInit() {
        this.load();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ((changes.subscriptionId && !changes.subscriptionId.firstChange) ||
            (changes.projectId && !changes.projectId.firstChange)) {
            this.load();
        }
    }

    load() {
        this.loading = true;
        this.error = null;
        this.savedSecret = null;
        this.showDetails = false;

        const subPromise = this.subscriptionId > 0
            ? this.service.getSubscription(this.subscriptionId)
            : Promise.resolve(null);

        Promise.all([this.service.getEventCatalog(), subPromise])
            .then(([catalogResponse, sub]) => {
                const rawCatalog: EventDefinition[] = (catalogResponse && catalogResponse.items) ? catalogResponse.items : [];
                // Site-only events (e.g. account.user.confirmed) have no project context and can
                // only be subscribed to site-wide — hide them when editing a project-scoped
                // subscription. Site Admin (projectId === null) sees the full catalog, since a
                // site-wide subscription can legitimately target project events too.
                const catalog = this.projectId !== null
                    ? rawCatalog.filter(e => e.scope !== 'site')
                    : rawCatalog;
                this.catalog = catalog;
                this.buildCatalogGroups(catalog);

                if (sub) {
                    this.form.name           = sub.name;
                    this.form.enabled        = sub.enabled;
                    this.form.event_pattern  = sub.event_pattern;
                    this.form.actionType     = (sub.action_type === 'email') ? 'email' : 'webhook';
                    this.form.retention_days = sub.retention_days;

                    const cfg = sub.action_config as any;
                    if (this.form.actionType === 'webhook') {
                        this.form.url    = (cfg && cfg.url)    || '';
                        this.form.method = (cfg && cfg.method) || 'POST';
                    } else if (this.form.actionType === 'email') {
                        this.form.recipients      = (cfg && cfg.recipients)        ? cfg.recipients.join(', ') : '';
                        this.form.subjectTemplate = (cfg && cfg.subject_template)  || '';
                        this.form.bodyTemplate    = (cfg && cfg.body_template)     || '';
                    } else if (this.form.actionType === 'llm') {
                        this.form.promptTemplate  = (cfg && cfg.prompt_template)   || '';
                    }

                    if (sub.retry_policy) {
                        this.form.overrideRetry   = true;
                        this.form.max_attempts    = sub.retry_policy.max_attempts;
                        this.form.backoff_seconds = sub.retry_policy.backoff_seconds.slice();
                    } else {
                        this.form.overrideRetry = false;
                    }
                    this.onPatternChange(sub.event_pattern);
                }

                this.loading = false;
            })
            .catch(err => {
                this.error = (err && err.error && err.error.message) || 'Failed to load';
                this.loading = false;
            });
    }

    buildCatalogGroups(catalog: EventDefinition[]) {
        const byCategory = _.groupBy(catalog, 'category');
        this.catalogGroups = Object.keys(byCategory).sort().map(cat => {
            const byEntity = _.groupBy(byCategory[cat], 'entity');
            const entityGroups: EntityGroup[] = Object.keys(byEntity).sort().map(ent => ({
                entity: ent,
                entityLabel: ENTITY_LABELS[ent] || ent,
                wildcardType: `${cat}.${ent}.*`,
                events: byEntity[ent],
            }));
            return {
                category: cat,
                categoryLabel: CATEGORY_LABELS[cat] || cat,
                wildcardType: `${cat}.*`,
                entityGroups,
            };
        });
    }

    onPatternChange(pattern: string) {
        this.selectedEvent = this.catalog.find(e => e.type === pattern) || null;
        this.showDetails = false;
    }

    onActionTypeChange() {
        // Reset action-specific fields when switching type so stale data isn't submitted.
        this.form.url            = '';
        this.form.method         = 'POST';
        this.form.recipients     = '';
        this.form.subjectTemplate = '';
        this.form.bodyTemplate   = '';
        this.form.promptTemplate = '';
    }

    onMaxAttemptsChange() {
        this.syncBackoffArray();
    }

    syncBackoffArray() {
        if (!this.form.overrideRetry) { return; }
        const needed = Math.max(0, this.form.max_attempts - 1);
        while (this.form.backoff_seconds.length < needed) {
            this.form.backoff_seconds.push(60);
        }
        this.form.backoff_seconds = this.form.backoff_seconds.slice(0, needed);
    }

    toggleOverrideRetry() {
        if (this.form.overrideRetry) {
            this.syncBackoffArray();
        }
    }

    insertPlaceholder(token: string, el: HTMLInputElement | HTMLTextAreaElement, field: 'url' | 'subjectTemplate' | 'bodyTemplate' | 'promptTemplate') {
        const current = this.form[field] || '';
        const start = el.selectionStart ?? current.length;
        const end = el.selectionEnd ?? current.length;

        this.form[field] = current.slice(0, start) + token + current.slice(end);

        const cursorPos = start + token.length;
        setTimeout(() => {
            el.focus();
            el.setSelectionRange(cursorPos, cursorPos);
        });
    }

    regenerateSecret() {
        if (this.subscriptionId <= 0) { return; }
        this.saving = true;
        this.service.regenerateSecret(this.subscriptionId)
            .then(result => {
                this.savedSecret = result.secret || null;
                this.saving = false;
            })
            .catch(err => {
                this.error = (err && err.error && err.error.message) || 'Failed to regenerate secret';
                this.saving = false;
            });
    }

    save() {
        if (!this.form.event_pattern) {
            this.error = 'Please select an event pattern.';
            return;
        }
        this.saving = true;
        this.error = null;
        this.savedSecret = null;

        let actionConfig: any;
        if (this.form.actionType === 'email') {
            const recipients = this.form.recipients
                .split(',')
                .map(s => s.trim())
                .filter(s => s.length > 0);
            actionConfig = {
                recipients,
                subject_template: this.form.subjectTemplate,
                body_template:    this.form.bodyTemplate,
            };
        } else if (this.form.actionType === 'llm') {
            actionConfig = {
                prompt_template: this.form.promptTemplate,
            };
        } else {
            actionConfig = { url: this.form.url, method: this.form.method };
        }

        const payload: any = {
            name:           this.form.name,
            enabled:        this.form.enabled,
            event_pattern:  this.form.event_pattern,
            action_type:    this.form.actionType,
            action_config:  actionConfig,
            retention_days: this.form.retention_days,
        };

        if (this.subscriptionId > 0) {
            payload.id = this.subscriptionId;
        }

        if (this.projectId !== null) {
            payload.project_id = this.projectId;
        }

        if (this.form.overrideRetry) {
            payload.retry_policy = {
                max_attempts:    this.form.max_attempts,
                backoff_seconds: this.form.backoff_seconds,
            };
        }

        this.service.saveSubscription(payload)
            .then(result => {
                this.savedSecret = result.secret || null;
                this.saving = false;
                if (!this.savedSecret) {
                    this.saved.emit();
                }
            })
            .catch(err => {
                this.error = (err && err.error && err.error.message) || 'Save failed';
                this.saving = false;
            });
    }

    copySecret() {
        if (!this.savedSecret) { return; }
        navigator.clipboard.writeText(this.savedSecret).then(() => {
            this.secretCopied = true;
            setTimeout(() => { this.secretCopied = false; }, 2000);
        });
    }

    cancel() {
        this.cancelled.emit();
    }
}
